import { apiClient } from '../utils/axios-config';
import { UserPermissions, Permission, UserRole, Resource, Action } from '../types/permissions.types';

/**
 * API para gestión de permisos RBAC
 */
export const permissionsApi = {
  /**
   * Obtener permisos del usuario actual
   */
  async getCurrentUserPermissions(): Promise<UserPermissions> {
    const response = await apiClient.get('/permissions/current');
    return response.data;
  },

  /**
   * Obtener todos los permisos disponibles
   */
  async getAllPermissions(): Promise<Permission[]> {
    const response = await apiClient.get('/permissions');
    return response.data;
  },

  /**
   * Verificar si el usuario tiene un permiso específico
   */
  async checkPermission(resource: Resource, action: Action): Promise<boolean> {
    const response = await apiClient.post('/permissions/check', {
      resource,
      action,
    });
    return response.data.hasPermission;
  },

  /**
   * Obtener permisos por rol
   */
  async getPermissionsByRole(role: UserRole): Promise<Permission[]> {
    const response = await apiClient.get(`/permissions/role/${role}`);
    return response.data;
  },

  /**
   * Actualizar permisos de un usuario (solo admins)
   */
  async updateUserPermissions(userId: string, permissions: Permission[]): Promise<UserPermissions> {
    const response = await apiClient.put(`/permissions/users/${userId}`, {
      permissions,
    });
    return response.data;
  },

  /**
   * Actualizar permisos de un rol (solo super admins)
   */
  async updateRolePermissions(role: UserRole, permissions: Permission[]): Promise<Permission[]> {
    const response = await apiClient.put(`/permissions/roles/${role}`, {
      permissions,
    });
    return response.data;
  },

  /**
   * Obtener usuarios con un permiso específico
   */
  async getUsersWithPermission(resource: Resource, action: Action): Promise<string[]> {
    const response = await apiClient.get(`/permissions/users`, {
      params: { resource, action },
    });
    return response.data.userIds;
  },

  /**
   * Crear un permiso personalizado (solo super admins)
   */
  async createCustomPermission(resource: Resource, action: Action, description: string): Promise<Permission> {
    const response = await apiClient.post('/permissions/custom', {
      resource,
      action,
      description,
    });
    return response.data;
  },

  /**
   * Eliminar un permiso personalizado (solo super admins)
   */
  async deleteCustomPermission(resource: Resource, action: Action): Promise<void> {
    await apiClient.delete(`/permissions/custom`, {
      data: { resource, action },
    });
  },

  /**
   * Obtener auditoría de permisos
   */
  async getPermissionsAudit(userId?: string, action?: string): Promise<any[]> {
    const response = await apiClient.get('/permissions/audit', {
      params: { userId, action },
    });
    return response.data;
  },
};

export default permissionsApi;
